import { useQuery } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";
import { DEFAULT_VALUE_BASIS, type ValueBasis } from "./concentration";
import type { ConcentrationResponse } from "./types";

/**
 * `facility` takes the same four values as /concentration — INTERNAL | EXTERNAL | CAP1 | CAP2.
 * It defaults to INTERNAL on both sides, so a call with no facility is byte-identical to the
 * internal-only endpoint this replaced.
 */
export async function fetchCapacity(
  facility = "INTERNAL",
  asOfDate: string,
  includeJoints = false,
  investor = "WAFRA",
  book = "ALL",
  product = "ALL",
  valueBasis: ValueBasis = DEFAULT_VALUE_BASIS,
): Promise<ConcentrationResponse> {
  const params = new URLSearchParams({ facility, as_of: asOfDate });
  if (includeJoints) params.set("include_joints", "1");
  // Omitted for Wafra so the default request URL is unchanged — see fetchConcentration.
  if (investor !== "WAFRA") params.set("investor", investor);
  // See fetchConcentration: passed on every scope so the filter is wired, not decorative.
  if (book && book !== "ALL") params.set("book", book);
  // Independent of `book`; the API intersects the two. See PRODUCT_FILTERS.
  if (product && product !== "ALL") params.set("product", product);
  // See fetchConcentration on why this is `valueBasis` and not `basis`.
  if (valueBasis !== DEFAULT_VALUE_BASIS) params.set("valueBasis", valueBasis);
  return getJson<ConcentrationResponse>(`${PRC_PREFIX}capacity?${params.toString()}`);
}

export function useCapacity(
  facility = "INTERNAL",
  asOfDate: string,
  includeJoints = false,
  investor = "WAFRA",
  book = "ALL",
  product = "ALL",
  valueBasis: ValueBasis = DEFAULT_VALUE_BASIS,
) {
  return useQuery({
    queryKey: ["prc-capacity", facility, asOfDate, includeJoints, investor, book, product,
               valueBasis],
    queryFn: () => fetchCapacity(facility, asOfDate, includeJoints, investor, book, product,
                                 valueBasis),
  });
}
